import PropTypes from '~/lib/propTypes'
import useLoggedInUser from '~/hooks/useLoggedInUser'
import {
  Text,
  OneTimeCodeInputV2 as OneTimeCodeInput,
  space,
  Button,
  Alert,
  Instruction,
} from '@ticketswap/solar'
import { useState } from 'react'
import styled from '@emotion/styled'
import { AUTHENTICATE_CLIENT_FOR_TOKEN } from '~/graphql/mutations/authenticateClientForToken'
import { useMutation } from '@apollo/client'
import getConfig from 'next/config'
import { AUTHENTICATE_USER_WITH_LOGIN_CODE } from '~/graphql/mutations/authenticateUserWithLoginCode'
import { setTrackingIdentity, track } from '~/utils/analytics'
import { AUTHENTICATION_COMPLETED } from '~/lib/tracking-events'
import { createMarkup } from '~/utils/createMarkup'
import { setCookie } from 'cookies-next'
import { MONTH_IN_SECONDS } from '~/lib/timeInSeconds'

const { publicRuntimeConfig } = getConfig()

const Form = styled.form`
  display: grid;
  grid-gap: ${space[16]};
`

const LoginWithCode = ({ t, onAuthenticationComplete }) => {
  const { email } = useLoggedInUser()
  const [code, setCode] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const [getAuthenticationToken, { client }] = useMutation(AUTHENTICATE_CLIENT_FOR_TOKEN)
  const [authenticate] = useMutation(AUTHENTICATE_USER_WITH_LOGIN_CODE)

  const handleAuthentication = async event => {
    event.preventDefault()

    setError(null)
    setLoading(true)

    const {
      data: {
        authenticateClientForToken: { token },
      },
    } = await getAuthenticationToken({
      variables: {
        input: {
          clientId: publicRuntimeConfig.NEXT_PUBLIC_CLIENT_ID,
        },
      },
    })

    const { data } = await authenticate({
      onError: async () => {
        setError({
          message: t('common:generic.somethingWentWrong'),
        })

        await track(AUTHENTICATION_COMPLETED, {
          method: 'code',
          isNew: true,
          error: 'SOMETHING_WENT_WRONG',
        })

        setLoading(false)
      },
      variables: {
        input: {
          email,
          code,
          token,
        },
      },
    })

    if (!data) return

    const {
      authenticateUserWithLoginCode: { errors, apiAccessToken },
    } = data

    if (errors.length) {
      setLoading(false)

      await track(AUTHENTICATION_COMPLETED, {
        method: 'code',
        isNew: false,
        error: errors[0].code,
      })

      return setError(errors[0])
    }

    const { tokenString } = apiAccessToken

    setCookie(process.env.NEXT_PUBLIC_TOKEN_COOKIE_NAME, tokenString, {
      maxAge: MONTH_IN_SECONDS,
      secure: true,
      sameSite: 'lax',
    })

    await client.resetStore()

    await setTrackingIdentity()

    track(AUTHENTICATION_COMPLETED, {
      method: 'code',
      isNew: false,
    })

    onAuthenticationComplete('code')
  }

  return (
    <Form onSubmit={handleAuthentication}>
      <Text
        as="p"
        dangerouslySetInnerHTML={createMarkup(
          t('common:auth.code.info', {
            email,
          }),
          {
            renderInline: true,
            disableAutolink: true,
          }
        )}
      />

      <div>
        <OneTimeCodeInput
          length={6}
          hideLabel
          id="one-time-code-input"
          legend={t('common:auth.code.label')}
          aria-label={t('common:auth.code.ariaLabel')}
          onChange={event => setCode(event.target.value)}
          instruction={<Instruction>{t('common:auth.code.help')}</Instruction>}
        />
      </div>

      {error && <Alert variant="error">{error.message}</Alert>}

      <Button loading={loading} variant="primary" disabled={code?.length !== 6}>
        Submit
      </Button>
    </Form>
  )
}

LoginWithCode.propTypes = {
  t: PropTypes.func.isRequired,
  onAuthenticationComplete: PropTypes.func.isRequired,
}

export default LoginWithCode
